/**
 * Derivation helpers for the consuming-cycle side of frozen specimen links.
 * `specimen.cycleId` remains the origin cycle; usage events identify a later
 * cycle that reserves, thaws, uses, transfers, or fertilizes the specimen.
 */

import type { Specimen, SpecimenUsage } from "./useSpecimens";
import { CONSUMING_SPECIMEN_USAGES } from "./frozenSpecimenUsages";
import type { TranslationFunction } from "@src/packages/i18n/translations";
import type { CycleType } from "@src/packages/cycles/hooks/useCycles";

/**
 * Step-config shape shared between the treatment-flow builder and the
 * frozen-flow rewriter. Kept structural (not imported from treatmentFlows)
 * to avoid a package-boundary cycle: this file is under `care`, that one
 * under `homepage`.
 */
type MinimalStepConfig = {
  id: string;
  title?: string;
  shortTitle?: string;
  description?: string;
  prompt?: string;
  skipText?: string;
  hasTriggerShot?: boolean;
  contentType?:
    "medications" | "procedure" | "monitoring" | "trigger" | "waiting";
  skippable?: boolean;
};

type FrozenSelectionUsage = Pick<
  SpecimenUsage,
  "usage" | "cycleId" | "hasDecisionAndConsent"
>;
type FrozenSelectionSpecimen = {
  id: Specimen["id"];
  type: Specimen["type"];
  specimenUsages: FrozenSelectionUsage[] | null;
};
type FrozenSpecimenType = FrozenSelectionSpecimen["type"];

export type FrozenCycleSelection = {
  usingFrozenSpecimens: boolean;
  specimenIds: string[];
  types: FrozenSpecimenType[];
};

export function getFrozenSpecimenSelectionForCycle(
  specimens: readonly FrozenSelectionSpecimen[] | undefined,
  cycleId: string | null | undefined,
): FrozenCycleSelection {
  if (!specimens || !cycleId) {
    return { usingFrozenSpecimens: false, specimenIds: [], types: [] };
  }

  const specimenIds: string[] = [];
  const types = new Set<FrozenSpecimenType>();

  for (const specimen of specimens) {
    const linked = (specimen.specimenUsages ?? []).some(
      (usage) =>
        usage.cycleId === cycleId && CONSUMING_SPECIMEN_USAGES.has(usage.usage),
    );
    if (!linked) continue;

    specimenIds.push(specimen.id);
    types.add(specimen.type);
  }

  return {
    usingFrozenSpecimens: specimenIds.length > 0,
    specimenIds,
    types: [...types],
  };
}

/**
 * Does this cycle open on frozen oocytes rather than on a fresh retrieval?
 *
 * Only frozen OOCYTES justify skipping stimulation and retrieval. A cycle
 * that carries frozen sperm still grows and collects her own eggs, so it
 * runs the full fresh-IVF flow. Three signals, any one sufficient:
 *
 * 1. A successful reservation left `reserve` usage events on oocyte
 *    specimens tied to this cycle — the strongest evidence.
 * 2. The cycle's own `contextSnapshot.materialIds` names at least one
 *    specimen whose loaded type is `oocyte`. This survives when the
 *    reservation write lagged or failed, but only fires when we can
 *    actually resolve the material to an oocyte. An unresolvable id, or
 *    ids that all resolve to sperm, do NOT trip the gate — that guarantee
 *    is what stops a fresh-IVF cycle with frozen sperm from losing its
 *    stimulation and retrieval steps.
 * 3. The accepted plan captured at cycle start says `eggState: "frozen"`
 *    (via `cycle.contextSnapshot.rawValues`) — the semantic answer Bassem
 *    confirmed is the right selector; the API keeps `eggState` on the plan
 *    but does not narrow the phase graph from it, so the frontend has to.
 */
type CycleContextView = {
  contextSnapshot?: {
    materialIds?: readonly string[] | null;
    rawValues?: Record<string, unknown> | null;
  } | null;
};

export function isFrozenOocyteCycle(
  cycle: CycleContextView | null | undefined,
  frozenSelection: FrozenCycleSelection,
  specimens?: readonly Pick<FrozenSelectionSpecimen, "id" | "type">[] | null,
): boolean {
  if (
    frozenSelection.usingFrozenSpecimens &&
    frozenSelection.types.includes("oocyte")
  ) {
    return true;
  }
  const materialIds = cycle?.contextSnapshot?.materialIds ?? [];
  if (materialIds.length > 0 && specimens) {
    const materialIdSet = new Set(materialIds);
    const hasOocyteMaterial = specimens.some(
      (specimen) =>
        materialIdSet.has(specimen.id) && specimen.type === "oocyte",
    );
    if (hasOocyteMaterial) return true;
  }
  const eggState = cycle?.contextSnapshot?.rawValues?.eggState;
  return eggState === "frozen";
}

/**
 * The IVF/ICSI step graph is authored fresh-cycle-first: it opens on ovarian
 * stimulation and moves through egg retrieval before it reaches fertilization.
 * When the patient carries frozen oocytes into that cycle she has already
 * done stimulation and retrieval — asking her to do them again is a bug she
 * cannot recover from without abandoning the cycle.
 *
 * The rewrite is presentation-only, and lives here so both the treatment hub
 * and the cycle-detail screen resolve the same list from the same source:
 *
 * - `egg_retrieval` is dropped. Nothing about it is honest for this cycle.
 * - `protocol` is kept and retitled to "Endometrial Preparation": the API's
 *   `protocol` phase carries the lining-prep meds she takes ahead of the
 *   transfer. Dropping the step altogether would strand those records; the
 *   retitle both keeps the breadcrumb short-label truthful ("Lining"
 *   instead of "Stim") and gives the meds she takes a real step to land on.
 * - `pre_treatment` copy is restated to describe pre-thaw coordination
 *   (birth control, priming) rather than stimulation.
 * - `fertilization` is renamed "Thaw & Fertilize" — the lab thaws the eggs
 *   and fertilizes them the same day, one event she should see as one step.
 *
 * The `protocol` step id is retained because it maps to the API's `protocol`
 * phase — the backend has no `endometrial_prep` phase and we do not want to
 * introduce a split that would break phase inference, procedure scheduling,
 * and the completion view. The relabel is enough to make the flow read
 * correctly: `pre_treatment → endometrial preparation → thaw & fertilize
 * → embryo_development → transfer → luteal_phase`.
 *
 * FET (frozen embryo transfer) has its own cycle type and its own graph;
 * this rewriter is a no-op for anything other than IVF or ICSI.
 */
export function applyFrozenOocyteFlowRewrites<T extends MinimalStepConfig>(
  steps: readonly T[],
  cycleType: CycleType | null | undefined,
  usingFrozenSpecimens: boolean,
  t: TranslationFunction,
): T[] {
  if (!usingFrozenSpecimens || (cycleType !== "ivf" && cycleType !== "icsi")) {
    return [...steps];
  }
  return steps
    .filter((step) => step.id !== "egg_retrieval")
    .map((step) => {
      if (step.id === "pre_treatment") {
        // The default pre-treatment copy names stimulation ("Birth control or
        // priming medications before stimulation"), which is not what she is
        // preparing for. Restate it as pre-thaw coordination without moving
        // the step, so the pre-treatment slot on her hub keeps its purpose
        // (medications she took ahead of the cycle) but reads honestly.
        return {
          ...step,
          description: t(
            "care.treatment_hub.frozen_oocyte_thaw.pre_treatment_description",
          ),
          prompt: t(
            "care.treatment_hub.frozen_oocyte_thaw.pre_treatment_prompt",
          ),
        };
      }
      if (step.id === "protocol") {
        // The stim step retitled to lining preparation — same phase id, but
        // the copy and breadcrumb now describe what her clinic actually does
        // on a frozen-egg cycle. Trigger shot is off: she is not producing
        // her own eggs on this cycle.
        return {
          ...step,
          title: t("care.treatment_hub.endometrial_prep.title"),
          shortTitle: t("care.treatment_hub.endometrial_prep.short_title"),
          description: t("care.treatment_hub.endometrial_prep.description"),
          prompt: t("care.treatment_hub.endometrial_prep.prompt"),
          skipText: t("care.treatment_hub.endometrial_prep.skip_text"),
          hasTriggerShot: false,
        };
      }
      if (step.id === "fertilization") {
        // Thaw and fertilization are one clinic event on one day. The step
        // title and copy state that; the eggs-fertilized field is filled in
        // by her and can be corrected downward for thaw or fertilization
        // attrition. The step's own field list (sperm source, method, count)
        // already fits — nothing to add.
        return {
          ...step,
          title: t(
            "care.treatment_hub.frozen_oocyte_thaw.fertilization_step_name",
          ),
          shortTitle: t(
            "care.treatment_hub.frozen_oocyte_thaw.fertilization_step_short",
          ),
          description: t(
            "care.treatment_hub.frozen_oocyte_thaw.fertilization_description",
          ),
        };
      }
      return step;
    });
}
